import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import {
  Loader2, FlaskConical, AlertTriangle, CheckCircle2, Sparkles,
} from "lucide-react";
import ArtifactQualityReview from "../../ArtifactQualityReview";
import CreditCostTag from "../../CreditCostTag";

export function BaselineTab({
  projectId, token,
  datasets, selectedDataset, setSelectedDataset,
  selectedColumn, setSelectedColumn,
  derivedMetrics, selectedDerivedMetric, setSelectedDerivedMetric,
  baseline, setBaseline,
  analyzing, calculateBaseline, fetchDerivedMetrics, getNumericColumns,
  aiGenerating, aiBaselineSuggestions, suggestBaselineMetrics,
}) {
  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold">Calculate Baseline Metrics</h3>
        {selectedDataset && (
          <Button variant="outline" size="sm" onClick={suggestBaselineMetrics} disabled={!!aiGenerating} className="gap-2" data-testid="ai-suggest-baseline-btn">
            {aiGenerating === "Baseline Suggestions" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            AI Suggest Metrics
            <CreditCostTag cost={5} />
          </Button>
        )}
      </div>

      {/* AI Baseline Suggestions */}
      {aiBaselineSuggestions && !aiBaselineSuggestions.ai_unavailable && (
        <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-3" data-testid="ai-baseline-suggestions-panel">
          <p className="text-xs font-medium text-primary uppercase tracking-wider">AI Metric Recommendations</p>
          {aiBaselineSuggestions.primary_y?.column && (
            <div className="p-3 bg-background border border-border rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">Primary Y</span>
                <span className="text-sm font-medium">{aiBaselineSuggestions.primary_y.column}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{aiBaselineSuggestions.primary_y.rationale}</p>
              <p className="text-xs text-muted-foreground">{aiBaselineSuggestions.primary_y.baseline_approach}</p>
            </div>
          )}
          {aiBaselineSuggestions.secondary_metrics?.map((m, i) => (
            <div key={i} className="flex items-center gap-3 p-2 bg-background border border-border rounded text-xs">
              <span className="px-2 py-0.5 rounded-full bg-primary/15 text-primary">{m.role?.replace("_", " ")}</span>
              <span className="font-medium">{m.column}</span>
              <span className="text-muted-foreground flex-1">{m.rationale}</span>
            </div>
          ))}
          {aiBaselineSuggestions.derived_metrics_suggested?.length > 0 && (
            <div className="pt-2 border-t border-border space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Suggested Derived Metrics</p>
              {aiBaselineSuggestions.derived_metrics_suggested.map((d, i) => (
                <p key={i} className="text-xs p-2 bg-background border border-border rounded">
                  <strong>{d.name}:</strong> {d.numerator} / {d.denominator} &mdash; {d.rationale}
                </p>
              ))}
            </div>
          )}
          {aiBaselineSuggestions.analysis_plan && (
            <p className="text-xs text-muted-foreground italic">{aiBaselineSuggestions.analysis_plan}</p>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Select Dataset</Label>
            <select
              value={selectedDataset?.dataset_id || ""}
              onChange={(e) => {
                const ds = datasets.find((d) => d.dataset_id === e.target.value);
                setSelectedDataset(ds);
                setSelectedColumn("");
                setSelectedDerivedMetric(null);
                setBaseline(null);
                fetchDerivedMetrics(e.target.value);
              }}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              data-testid="baseline-dataset-select"
            >
              <option value="">Select a dataset</option>
              {datasets.map((ds) => (
                <option key={ds.dataset_id} value={ds.dataset_id}>{ds.filename}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label>Select Measure Column</Label>
            <select
              value={selectedColumn}
              onChange={(e) => {
                setSelectedColumn(e.target.value);
                const dm = derivedMetrics.find((m) => m.metric_id === e.target.value);
                setSelectedDerivedMetric(dm || null);
              }}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              disabled={!selectedDataset}
              data-testid="baseline-column-select"
            >
              <option value="">Select a column</option>
              {derivedMetrics.length > 0 && (
                <optgroup label="Derived Metrics">
                  {derivedMetrics.map((dm) => (
                    <option key={dm.metric_id} value={dm.metric_id}>{dm.name} ({dm.numerator_column}/{dm.denominator_column})</option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Raw Columns">
                {getNumericColumns().map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </optgroup>
            </select>
            {selectedDerivedMetric && (
              <div className="flex items-center gap-2 mt-1" data-testid="baseline-derived-badge">
                <FlaskConical className="w-3 h-3 text-primary" />
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/15 text-primary">Derived</span>
                <span className="text-[10px] text-muted-foreground">
                  {selectedDerivedMetric.numerator_column} / {selectedDerivedMetric.denominator_column}
                  {selectedDerivedMetric.unit_label && ` (${selectedDerivedMetric.unit_label})`}
                </span>
              </div>
            )}
          </div>

          <Button onClick={calculateBaseline} disabled={!selectedDataset || !selectedColumn || analyzing} className="w-full" data-testid="calculate-baseline-btn">
            {analyzing ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Calculating...</>) : "Calculate Baseline"}
          </Button>
        </div>

        {/* Baseline Results */}
        {baseline && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h4 className="font-medium">Baseline Statistics: {baseline.column}</h4>
              {baseline.is_derived && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/15 text-primary" data-testid="baseline-result-derived-badge">Derived</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Mean", value: baseline.mean },
                { label: "Median", value: baseline.median },
                { label: "Std Dev", value: baseline.std },
                { label: "Range", value: baseline.range },
                { label: "Min", value: baseline.min },
                { label: "Max", value: baseline.max },
              ].map((stat) => (
                <div key={stat.label} className="bg-background border border-border rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                  <p className="text-lg font-bold">{stat.value}</p>
                </div>
              ))}
            </div>
            {baseline.normality_test && (
              <div className={`p-3 rounded-lg border ${baseline.normality_test.is_normal ? "border-[hsl(var(--success))] bg-[hsl(var(--success))]/10" : "border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10"}`}>
                <div className="flex items-center gap-2">
                  {baseline.normality_test.is_normal ? <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))]" /> : <AlertTriangle className="w-4 h-4 text-[hsl(var(--warning))]" />}
                  <span className="font-medium text-sm">{baseline.normality_test.is_normal ? "Data appears normally distributed" : "Data may not be normally distributed"}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Shapiro-Wilk p-value: {baseline.normality_test.p_value}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedDataset && <ArtifactQualityReview projectId={projectId} artifactType="dataset" token={token} className="mt-4" />}
      {baseline && <ArtifactQualityReview projectId={projectId} artifactType="baseline_statistics" token={token} className="mt-4" />}
    </div>
  );
}
